import { useMemo } from "react";

import type { MeasurementType, ToolType } from "@/viewport-tools/measurement";
import { MEASUREMENT_TYPES, TOOL_IDS } from "@/viewport-tools/measurement";
import { VIEWPORT_TOOLSET_IDS, type ViewportToolsetId } from "@/viewport-tools/core/contracts";
import { useMeasurementToolsetRailController } from "@/viewport-tools/measurement";
import type { ViewportToolsetRailControllerMap } from "@/viewport-tools/rail/types";
import type { TagToolId } from "@/viewport-tools/tags";
import { useScaleToolsetRailController } from "@/viewport-tools/scale";
import { useTagsToolsetRailController } from "@/viewport-tools/tags";
import { useViewportToolsetControllers } from "@/viewport-tools/use-viewport-toolset-controllers";
import {
  VIEWPORT_VIEW_IDS,
  type ViewportViewId,
} from "@/viewport-tools/view-config";
import type { useMeasurementScale } from "@/hooks/useMeasurementScale";

interface UseTakeoffViewportToolsetControllersArgs {
  viewId?: ViewportViewId;
  measurementScale: ReturnType<typeof useMeasurementScale>;
  activeToolsetId: ViewportToolsetId | null;
  onActiveToolsetChange: (toolsetId: ViewportToolsetId | null) => void;
  activeTool: ToolType | null;
  activeMeasurementType: MeasurementType;
  onMeasurementToolChange: (tool: ToolType | null) => void;
  onMeasurementTypeChange: (type: MeasurementType) => void;
  snapEnabled: boolean;
  onSnapToggle: () => void;
  snapAvailable?: boolean;
  angleLockEnabled: boolean;
  onAngleLockToggle: () => void;
  incrementSnapEnabled: boolean;
  onIncrementSnapToggle: () => void;
  incrementStepFeet: number;
  onIncrementStepChange: (stepFeet: number) => void;
  heightValue: string;
  onHeightChange: (value: string) => void;
  textLocked?: boolean;
  onTextLockToggle?: () => void;
  textFontSize?: number;
  onTextFontSizeChange?: (value: number) => void;
  activeTagTool: TagToolId | null;
  tagOptions: Array<{ id: string; label: string }>;
  selectedTagOptionId: string | null;
  onSelectedTagOptionChange: (tagId: string) => void;
  showOverlays: boolean;
  onTagsToolSelect: (toolId: TagToolId | null) => void;
  pagePixelWidth: number;
  pagePixelHeight: number;
}

export function useTakeoffViewportToolsetControllers({
  viewId = VIEWPORT_VIEW_IDS.takeoffPilot,
  measurementScale,
  activeToolsetId,
  onActiveToolsetChange,
  activeTool,
  activeMeasurementType,
  onMeasurementToolChange,
  onMeasurementTypeChange,
  snapEnabled,
  onSnapToggle,
  snapAvailable,
  angleLockEnabled,
  onAngleLockToggle,
  incrementSnapEnabled,
  onIncrementSnapToggle,
  incrementStepFeet,
  onIncrementStepChange,
  heightValue,
  onHeightChange,
  textLocked,
  onTextLockToggle,
  textFontSize,
  onTextFontSizeChange,
  activeTagTool,
  tagOptions,
  selectedTagOptionId,
  onSelectedTagOptionChange,
  showOverlays,
  onTagsToolSelect,
  pagePixelWidth,
  pagePixelHeight,
}: UseTakeoffViewportToolsetControllersArgs) {
  const showHeightInput =
    activeMeasurementType === MEASUREMENT_TYPES.CF ||
    ((activeTool === TOOL_IDS.line || activeTool === TOOL_IDS.polyline) &&
      activeMeasurementType === MEASUREMENT_TYPES.SQFT);
  const isScaleExpanded = activeToolsetId === VIEWPORT_TOOLSET_IDS.scale;
  const isMeasurementExpanded = activeToolsetId === VIEWPORT_TOOLSET_IDS.measurement;
  const isTagsExpanded = activeToolsetId === VIEWPORT_TOOLSET_IDS.tags;

  const scaleController = useScaleToolsetRailController({
    expanded: isScaleExpanded,
    onExpandedChange: (expanded) =>
      onActiveToolsetChange(expanded ? VIEWPORT_TOOLSET_IDS.scale : null),
    calibrationState: measurementScale.calibrationState,
    currentScale: measurementScale.scale,
    customDistanceInput: measurementScale.customDistanceInput,
    setCustomDistanceInput: measurementScale.setCustomDistanceInput,
    startCalibration: measurementScale.startCalibration,
    applyCustomDistance: measurementScale.applyCustomDistance,
    applyPresetScale: measurementScale.applyPresetScale,
    cancelCalibration: measurementScale.cancelCalibration,
    pagePixelWidth,
    pagePixelHeight,
  });

  const measurementController = useMeasurementToolsetRailController({
    scale: measurementScale.scale,
    expanded: isMeasurementExpanded,
    onExpandedChange: (expanded) =>
      onActiveToolsetChange(expanded ? VIEWPORT_TOOLSET_IDS.measurement : null),
    activeTool,
    activeMeasurementType,
    onToolChange: onMeasurementToolChange,
    onMeasurementTypeChange,
    snapEnabled,
    onSnapToggle,
    snapAvailable,
    angleLockEnabled,
    onAngleLockToggle,
    incrementSnapEnabled,
    onIncrementSnapToggle,
    incrementStepFeet,
    onIncrementStepChange,
    heightValue,
    onHeightChange,
    showHeightInput,
    textLocked,
    onTextLockToggle,
    textFontSize,
    onTextFontSizeChange,
  });

  const tagsController = useTagsToolsetRailController({
    expanded: isTagsExpanded,
    onExpandedChange: (expanded) =>
      onActiveToolsetChange(expanded ? VIEWPORT_TOOLSET_IDS.tags : null),
    activeTool: activeTagTool,
    disabled: !showOverlays,
    tagOptions,
    selectedTagOptionId,
    onSelectedTagOptionChange,
    onToolSelect: onTagsToolSelect,
  });

  const allControllers = useMemo<ViewportToolsetRailControllerMap>(
    () => ({
      scale: scaleController,
      measurement: measurementController,
      tags: tagsController,
    }),
    [measurementController, scaleController, tagsController]
  );

  return useViewportToolsetControllers(
    viewId,
    allControllers
  );
}
